import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { FilterChip, StatusChip } from '@/components/ui/Chip';
import { Textarea } from '@/components/ui/Textarea';
import {
  TextPlaceholderProvider,
  TextPlaceholderToggle,
} from '@/components/patientReports/TextPlaceholderPanel';
import {
  patientReportTextErrorMessage,
  type PatientReportText,
  type PatientReportTextType,
} from '@/hooks/usePatientReportTexts';
import { textTypeTaken } from '@/lib/patientReports/userTexts';
import { FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function ReportTextLibrary({
  types,
  texts,
  loading,
  error,
  busy,
  disabled,
  usedTextIds,
  onSaveText,
  onDeleteText,
  onInsert,
}: {
  types: readonly PatientReportTextType[];
  texts: readonly PatientReportText[];
  loading: boolean;
  error: boolean;
  busy: boolean;
  disabled?: boolean;
  usedTextIds: ReadonlySet<string>;
  onSaveText: (input: { id?: string; typeId: string; body: string }) => Promise<void>;
  onDeleteText: (id: string) => Promise<void>;
  onInsert: (text: PatientReportText) => void;
}) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [draftTypeId, setDraftTypeId] = useState<string | null>(null);
  const [draftBody, setDraftBody] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const labels = {
    duplicate: t('patientReports.texts_duplicate'),
    cpr: t('patientReports.texts_cpr_blocked'),
    single: t('patientReports.texts_single'),
  };

  const create = async () => {
    const chosen = types.find((type) => type.id === draftTypeId);
    if (!chosen || textTypeTaken(chosen, texts)) {
      setFormError(
        chosen && textTypeTaken(chosen, texts)
          ? t('patientReports.texts_single')
          : t('patientReports.texts_tag_required'),
      );
      return;
    }
    if (!draftBody.trim()) {
      setFormError(t('patientReports.texts_body_required'));
      return;
    }
    setFormError(null);
    try {
      await onSaveText({ typeId: chosen.id, body: draftBody });
      setDraftTypeId(null);
      setDraftBody('');
    } catch (caught) {
      setFormError(
        patientReportTextErrorMessage(caught, t('patientReports.texts_save_error'), labels),
      );
    }
  };

  return (
    <TextPlaceholderProvider anchorRef={anchorRef}>
      <div ref={anchorRef} data-testid="report-text-library" className="flex flex-col gap-s3">
        {loading ? (
          <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.loading')}</p>
        ) : null}
        {error ? (
          <p className={cn(TYPE.small, 'text-ds-red-ink')}>{t('patientReports.texts_error')}</p>
        ) : null}
        {!loading && types.length === 0 ? (
          <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.texts_empty')}</p>
        ) : null}
        {types.length > 0 ? (
          <div className="flex flex-col gap-s2 rounded-ctl border border-ds-line bg-ds-sunk p-s3">
            <p className={cn(TYPE.body, 'font-semibold text-ds-text')}>
              {t('patientReports.texts_new')}
            </p>
            <TypeTags
              types={types}
              texts={texts}
              value={draftTypeId}
              disabled={disabled || busy}
              onChange={(typeId) => {
                setDraftTypeId(typeId);
                setFormError(null);
              }}
            />
            <PersonalTextField
              id="report-text-draft-body"
              value={draftBody}
              disabled={disabled || busy}
              onChange={setDraftBody}
            />
            {formError ? <p className={cn(TYPE.small, 'text-ds-red-ink')}>{formError}</p> : null}
            <Button type="button" disabled={disabled} loading={busy} onClick={() => void create()}>
              {t('patientReports.texts_save')}
            </Button>
          </div>
        ) : null}
        {texts.map((text) => {
          const type = types.find((item) => item.id === text.typeId);
          return (
            <TextCard
              key={`${text.id}:${text.body}`}
              text={text}
              typeName={type?.name ?? ''}
              inserted={usedTextIds.has(text.id)}
              busy={busy}
              disabled={disabled}
              onSaveText={onSaveText}
              onDeleteText={onDeleteText}
              onInsert={() => onInsert(text)}
            />
          );
        })}
      </div>
    </TextPlaceholderProvider>
  );
}

function TypeTags({
  types,
  texts,
  value,
  disabled,
  onChange,
}: {
  types: readonly PatientReportTextType[];
  texts: readonly PatientReportText[];
  value: string | null;
  disabled?: boolean;
  onChange: (typeId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset disabled={disabled} className="min-w-0">
      <legend className={FORM_LABEL_TYPE}>{t('patientReports.texts_tag')}</legend>
      <p className={cn('mt-s1', FORM_HINT_TYPE)}>{t('patientReports.texts_tag_hint')}</p>
      <div className="mt-s2 flex flex-wrap gap-s2">
        {types.map((type) => {
          const taken = textTypeTaken(type, texts);
          if (taken) {
            return (
              <button
                key={type.id}
                type="button"
                disabled
                aria-disabled="true"
                data-testid={`report-text-tag-${type.id}`}
                title={t('patientReports.texts_tag_taken_hint')}
                className={cn(
                  TYPE.control,
                  'inline-flex h-8 cursor-not-allowed items-center gap-s2 rounded-full border border-ds-field bg-ds-sunk px-s3 font-medium text-ds-muted',
                )}
              >
                <span>{type.name}</span>
                <span className={TYPE.meta}>{t('patientReports.texts_tag_taken')}</span>
              </button>
            );
          }
          return (
            <FilterChip key={type.id} pressed={value === type.id} onClick={() => onChange(type.id)}>
              {type.name}
            </FilterChip>
          );
        })}
      </div>
    </fieldset>
  );
}

function PersonalTextField({
  id,
  value,
  disabled,
  onChange,
}: {
  id: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const hintId = `${id}-hint`;
  return (
    <div>
      <Textarea
        id={id}
        label={t('patientReports.texts_body')}
        aria-describedby={hintId}
        value={value}
        maxLength={20000}
        disabled={disabled}
        className="min-h-24"
        onChange={(event) => onChange(event.target.value)}
      />
      <p id={hintId} className={cn('mt-s1', FORM_HINT_TYPE)}>
        {t('patientReports.texts_body_hint')}
      </p>
      <TextPlaceholderToggle />
    </div>
  );
}

function TextCard({
  text,
  typeName,
  inserted,
  busy,
  disabled,
  onSaveText,
  onDeleteText,
  onInsert,
}: {
  text: PatientReportText;
  typeName: string;
  inserted: boolean;
  busy: boolean;
  disabled?: boolean;
  onSaveText: (input: { id?: string; typeId: string; body: string }) => Promise<void>;
  onDeleteText: (id: string) => Promise<void>;
  onInsert: () => void;
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState(text.body);
  const [rowError, setRowError] = useState<string | null>(null);
  const labels = {
    duplicate: t('patientReports.texts_duplicate'),
    cpr: t('patientReports.texts_cpr_blocked'),
    single: t('patientReports.texts_single'),
  };

  const run = async (action: () => Promise<void>) => {
    setRowError(null);
    try {
      await action();
    } catch (caught) {
      setRowError(
        patientReportTextErrorMessage(caught, t('patientReports.texts_save_error'), labels),
      );
    }
  };

  return (
    <div
      data-testid={`report-text-${text.id}`}
      className="flex flex-col gap-s2 rounded-ctl border border-ds-line p-s3"
    >
      {typeName ? <StatusChip tone="neutral">{typeName}</StatusChip> : null}
      <PersonalTextField
        id={`report-text-body-${text.id}`}
        value={body}
        disabled={disabled || busy}
        onChange={setBody}
      />
      {rowError ? <p className={cn(TYPE.small, 'text-ds-red-ink')}>{rowError}</p> : null}
      <div className="flex flex-wrap gap-s2">
        <Button
          type="button"
          variant="secondary"
          disabled={disabled || busy}
          onClick={() => void run(() => onSaveText({ id: text.id, typeId: text.typeId, body }))}
        >
          {t('patientReports.texts_save')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled || inserted}
          data-testid={`report-text-insert-${text.id}`}
          onClick={onInsert}
        >
          {inserted ? t('patientReports.texts_inserted_already') : t('patientReports.texts_insert')}
        </Button>
        <Button
          type="button"
          variant="tertiary"
          disabled={disabled || busy}
          onClick={() => {
            if (!window.confirm(t('patientReports.texts_delete_mine_confirm'))) return;
            void run(() => onDeleteText(text.id));
          }}
        >
          {t('patientReports.texts_delete_mine')}
        </Button>
      </div>
    </div>
  );
}
